package com.shadowings.gero.presentation.screen.characters

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.BG_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.BODY_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EAR_LEFT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EAR_RIGHT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.EYES_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.HEAD_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIA_LEFT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIA_RIGHT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.MACCHIE_TYPE_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.WHISKERS_KEY
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.Lock
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.builder_pro_unlock_btn
import gero.composeapp.generated.resources.chiudi_btn
import gero.composeapp.generated.resources.custom_hat_locked_caption
import gero.composeapp.generated.resources.custom_macchie_type_base
import gero.composeapp.generated.resources.custom_macchie_type_tigrato
import gero.composeapp.generated.resources.custom_macchie_type_tuxedo
import gero.composeapp.generated.resources.custom_macchie_type_tuxedo_tigrato
import gero.composeapp.generated.resources.custom_pattern_locked_desc
import gero.composeapp.generated.resources.custom_pattern_locked_title
import gero.composeapp.generated.resources.custom_title_bg
import gero.composeapp.generated.resources.custom_title_body
import gero.composeapp.generated.resources.custom_title_ear_left
import gero.composeapp.generated.resources.custom_title_ear_right
import gero.composeapp.generated.resources.custom_title_eyes
import gero.composeapp.generated.resources.custom_title_face
import gero.composeapp.generated.resources.custom_title_head
import gero.composeapp.generated.resources.custom_title_macchie_type
import gero.composeapp.generated.resources.custom_title_spot_left
import gero.composeapp.generated.resources.custom_title_spot_right
import gero.composeapp.generated.resources.custom_title_whiskers
import gero.composeapp.generated.resources.macchie
import gero.composeapp.generated.resources.tigrato
import gero.composeapp.generated.resources.tuxedo
import gero.composeapp.generated.resources.tuxedo_tigrato
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun NiloCharacterScreenPreview() {
    AppTheme {
        Surface {
            NiloCharacterScreen(
                userData = UserData(niloColors = mapOf()),
                onNiloColorChanged = { _, _ -> }
            )
        }
    }
}

/** level=0 (below every pattern requirement): shows the pattern picker with locks + captions. */
@Preview(name = "Patterns locked")
@Composable
private fun NiloCharacterScreenPatternsLockedPreview() {
    AppTheme {
        Surface {
            NiloCharacterScreen(
                userData = UserData(niloColors = mapOf(), experience = 0),
                onNiloColorChanged = { _, _ -> }
            )
        }
    }
}

@Composable
fun NiloCharacterScreen(
    userData: UserData,
    onNiloColorChanged: (categoryKey: String, index: Int) -> Unit,
    onResetClick: () -> Unit = {},
    onPurchaseFullVersion: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val bgColorIndex =
        (userData.niloColors[BG_KEY] ?: 0).coerceIn(0, backgroundOptions.size - 1)
    val bodyColorIndex = (userData.niloColors[BODY_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val earLeftColorIndex =
        (userData.niloColors[EAR_LEFT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val earRightColorIndex =
        (userData.niloColors[EAR_RIGHT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val headColorIndex = (userData.niloColors[HEAD_KEY] ?: 0).coerceIn(0, hairOptions.size - 1)
    val macchiaRightColorIndex =
        (userData.niloColors[MACCHIA_RIGHT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val macchiaLeftColorIndex =
        (userData.niloColors[MACCHIA_LEFT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val eyesColorIndex = (userData.niloColors[EYES_KEY] ?: 7).coerceIn(0, eyesOptions.size - 1)
    val whiskersColorIndex =
        (userData.niloColors[WHISKERS_KEY] ?: 7).coerceIn(0, hatBarOptions.size - 1)
    val macchieTypeIndex = (userData.niloColors[MACCHIE_TYPE_KEY] ?: 0).coerceIn(0, 3)

    // livello minimo richiesto per ogni motivo, in ordine di opzione (0 = nessun requisito)
    val macchieTypeLevelRequirements = listOf(0, 6, 12, 24)
    val userLevel = GameConstants.getPlayerLevel(userData.experience)
    var lockedMacchieRequiredLevel by remember { mutableStateOf<Int?>(null) }

    val selectedFace = remember { mutableStateOf(Faces.Idle) }

    val deviceInfo = rememberDeviceInfo()
    val isPhoneLargePortrait =
        deviceInfo.deviceClass == DeviceClass.PHONE_LARGE && !deviceInfo.isLandscape
    val avatarSize = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 80.dp
        DeviceClass.PHONE_LARGE -> if (isPhoneLargePortrait) 120.dp else 110.dp
        DeviceClass.TABLET_MEDIUM -> 120.dp
        DeviceClass.TABLET_LARGE -> 150.dp
        DeviceClass.WEB_FULL -> 150.dp
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(Modifier.size(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                Modifier.background(
                    color = backgroundOptions[bgColorIndex],
                    shape = RoundedCornerShape(6.dp)
                ).padding(top = 6.dp, start = 6.dp, end = 6.dp)
            ) {
                val avatarModifier = Modifier
                    .height(avatarSize)
                    .align(Alignment.BottomCenter)
                NiloAvatar(
                    userData = userData,
                    face = selectedFace.value,
                    modifier = avatarModifier
                )
            }

            Spacer(Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.custom_title_face),
                    style = MaterialTheme.typography.titleMedium
                )
                Spacer(Modifier.height(6.dp))
                FaceMenu(
                    selectedFace = selectedFace.value,
                    onFaceSelected = { selectedFace.value = it },
                    faces = listOf(Faces.Idle, Faces.Sad, Faces.Sleeping, Faces.Surprised),
                    labelRes = { face -> face.labelRes() }
                )
            }
        }

        Spacer(Modifier.size(20.dp))

        val macchieTypeOptionsRaw = listOf(
            Res.drawable.macchie to stringResource(Res.string.custom_macchie_type_base),
            Res.drawable.tigrato to stringResource(Res.string.custom_macchie_type_tigrato),
            Res.drawable.tuxedo to stringResource(Res.string.custom_macchie_type_tuxedo),
            Res.drawable.tuxedo_tigrato to stringResource(Res.string.custom_macchie_type_tuxedo_tigrato),
        )
        val macchieTypeOptions =
            macchieTypeOptionsRaw.mapIndexed { index, (drawable, description) ->
                val requiredLevel = macchieTypeLevelRequirements[index]
                val locked =
                    requiredLevel > 0 && userLevel < requiredLevel && !userData.isFullVersionUnlocked
                HatTypeOptionData(
                    drawable = drawable,
                    contentDescription = description,
                    locked = locked,
                    lockCaption = if (locked) {
                        stringResource(Res.string.custom_hat_locked_caption, requiredLevel)
                    } else {
                        null
                    }
                )
            }

        val macchieTypeSectionContent: @Composable () -> Unit = {
            HatTypeSection(
                title = stringResource(Res.string.custom_title_macchie_type),
                options = macchieTypeOptions,
                selectedIndex = macchieTypeIndex,
                onSelect = { index -> onNiloColorChanged(MACCHIE_TYPE_KEY, index) },
                onLockedClick = { index ->
                    lockedMacchieRequiredLevel = macchieTypeLevelRequirements[index]
                }
            )
        }

        val sections = listOf(
            ColorSectionData(
                title = stringResource(Res.string.custom_title_bg),
                options = backgroundOptions,
                selectedIndex = bgColorIndex,
                onSelect = { onNiloColorChanged(BG_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_body),
                options = hairOptions,
                selectedIndex = bodyColorIndex,
                onSelect = { onNiloColorChanged(BODY_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_ear_left),
                options = hairOptions,
                selectedIndex = earLeftColorIndex,
                onSelect = { onNiloColorChanged(EAR_LEFT_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_ear_right),
                options = hairOptions,
                selectedIndex = earRightColorIndex,
                onSelect = { onNiloColorChanged(EAR_RIGHT_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_head),
                options = hairOptions,
                selectedIndex = headColorIndex,
                onSelect = { onNiloColorChanged(HEAD_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_spot_left),
                options = hairOptions,
                selectedIndex = macchiaLeftColorIndex,
                onSelect = { onNiloColorChanged(MACCHIA_LEFT_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_spot_right),
                options = hairOptions,
                selectedIndex = macchiaRightColorIndex,
                onSelect = { onNiloColorChanged(MACCHIA_RIGHT_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_eyes),
                options = eyesOptions,
                selectedIndex = eyesColorIndex,
                onSelect = { onNiloColorChanged(EYES_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_whiskers),
                options = hatBarOptions,
                selectedIndex = whiskersColorIndex,
                onSelect = { onNiloColorChanged(WHISKERS_KEY, it) }
            ),
        )
        ColorsPicker(
            sections = sections,
            onResetClick = onResetClick,
            headerContent = macchieTypeSectionContent
        )
    }

    val requiredLevel = lockedMacchieRequiredLevel
    if (requiredLevel != null) {
        AlertDialog(
            onDismissRequest = { lockedMacchieRequiredLevel = null },
            icon = {
                Icon(
                    imageVector = FeatherIcons.Lock,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
            },
            title = {
                Text(
                    text = stringResource(Res.string.custom_pattern_locked_title),
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
            },
            text = {
                Text(
                    text = stringResource(Res.string.custom_pattern_locked_desc, requiredLevel),
                    textAlign = TextAlign.Center
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        lockedMacchieRequiredLevel = null
                        onPurchaseFullVersion()
                    }
                ) {
                    Text(stringResource(Res.string.builder_pro_unlock_btn))
                }
            },
            dismissButton = {
                TextButton(onClick = { lockedMacchieRequiredLevel = null }) {
                    Text(stringResource(Res.string.chiudi_btn))
                }
            }
        )
    }
}
